import React, { useState, useEffect } from "react";
import ModalWrapper from "../../components/ui/modals/ModalWrapper";
import Button from "../../components/ui/buttons/Button";
import { updateSMSNumber, updateSMSPermission } from "../../services/api/core";
import {
  formatSMSPhoneNumberInput,
  validateSMSPhoneNumber,
} from "../../utils/auth";

/**
 * Onboarding modal for collecting SMS opt-in and a contact phone number before
 * the user receives dispatch text messages. Shown from Home when SMS preferences
 * have not yet been saved.
 *
 * @param {boolean} isOpen Modal visibility.
 * @param {Function} onClose Close without saving.
 * @param {Function} onComplete Called after preferences are saved successfully.
 * @param {string} initialPhoneNumber Pre-filled value from `user.sms_number`.
 * @returns {JSX.Element|null}
 */
const SMSPermissionsModal = ({
  isOpen,
  onClose,
  onComplete,
  initialPhoneNumber = "",
}) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [confirmChecked, setConfirmChecked] = useState(true);
  const [phoneError, setPhoneError] = useState("");
  const [permissionError, setPermissionError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePhoneChange = (e) => {
    const digits = formatSMSPhoneNumberInput(e.target.value, phoneNumber);

    setPhoneNumber(digits);

    if (validateSMSPhoneNumber(digits)) {
      setPhoneError("");
      setConfirmChecked(true);
    } else {
      setPhoneError("Number format should be 123-456-7890");
      setConfirmChecked(false);
    }
  };

  const handlePermissionChange = (e) => {
    setConfirmChecked(e.target.checked);
    if (e.target.checked) {
      setPermissionError("");
    }
  };

  const handleAccept = async () => {
    // Validate before submit
    const isPhoneValid = validateSMSPhoneNumber(phoneNumber);

    if (!isPhoneValid) {
      setPhoneError("Number format should be 123-456-7890");
      return;
    }

    if (!confirmChecked) {
      setPermissionError("Please check the box to give permission");
      return;
    }

    setIsSubmitting(true);

    try {
      // Update phone number
      await updateSMSNumber(phoneNumber);

      // Update permission to accepted (id=1)
      await updateSMSPermission("accepted");

      // Notify parent of successful completion
      onComplete();
    } catch (error) {
      console.error("Error updating SMS permissions:", error);
      // Could add error state here for user feedback
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);

    try {
      // Set permission to rejected (id=2)
      await updateSMSPermission("rejected");

      // Notify parent of completion
      onComplete();
    } catch (error) {
      console.error("Error rejecting SMS permissions:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber(initialPhoneNumber);
      setConfirmChecked(true);
      setPhoneError("");
      setPermissionError("");
    }
  }, [isOpen, initialPhoneNumber]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col text-center">
        <h2 className="text-2xl font-bold mb-4">Contact Permission</h2>
        <p className={`mt-2 text-xs ml-4 text-gray-600`}>
          <i>Cell Phone Number</i>
        </p>

        {/* Phone Number Input */}
        <div className="relative">
          <input
            type="tel"
            className={`text-2xl p-2 h-[36px] w-full border text-center rounded-md ${
              phoneError ? "border-red-500" : "border-gray-300"
            }`}
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            placeholder="123-456-7890"
            value={phoneNumber}
            onChange={handlePhoneChange}
            disabled={isSubmitting}
          />
          {phoneNumber && (
            <button
              type="button"
              onClick={() => {
                setPhoneNumber("");
                setPhoneError("Number format should be 123-456-7890");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              ✕
            </button>
          )}
        </div>

        {/* Phone validation message */}
        <p
          className={`mt-2 text-xs ml-4 ${phoneError ? "text-red-600" : "text-gray-600"}`}
        >
          <i>{phoneError || "Number format should be 123-456-7890"}</i>
        </p>


        {/* Permission Checkbox */}
        <div className="bg-gray-100 rounded-md mt-4 p-4">
          <div
            className={`flex items-center gap-3 p-3 rounded-md ${confirmChecked ? "bg-green-100" : "bg-gray-200"}`}
          >
            <input
              type="checkbox"
              id="smsPermission"
              className="w-5 h-5"
              checked={confirmChecked}
              onChange={handlePermissionChange}
              disabled={isSubmitting}
            />
            <label htmlFor="smsPermission" className="text-sm flex-1">
              I give permission to receive shift scheduling SMS messages from
              R&S Construction App
            </label>
          </div>

          <p className="text-xs mt-3 text-gray-600">
            By checking this box, you agree to receive shift scheduling SMS
            messages from the R&S Construction App, which inform you as soon as
            possible where and when you will be working. You can opt out at any
            time by replying STOP or unchecking the box in the app. Msg & data
            rates may apply.{" "}
            <a
              href="/app/sms-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-900 hover:underline"
            >
              Click to see Privacy Policy and Terms (new tab)
            </a>
          </p>
        </div>

        {/* Permission error message */}
        {permissionError && (
          <p className="mt-2 text-sm text-red-600">{permissionError}</p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleReject}
            text={isSubmitting ? "Processing..." : "REJECT Permission"}
            variant={isSubmitting || confirmChecked ? "disabled" : "secondary"}
            className="w-full"
          />
          <Button
            onClick={handleAccept}
            text={isSubmitting ? "Processing..." : "Confirm Permission"}
            variant={isSubmitting || !confirmChecked ? "disabled" : "primary"}
            className="w-full"
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SMSPermissionsModal;
